import axios from 'axios'
import { API_BASE_URL } from '../constants/system'
import type { LoginResponse } from '../types/app'

/**
 * Two-step login (#1724, design/2026-10-27-mfa.md): an authenticator-app
 * code after the password. POST /user/login answers an account that has set
 * it up (or any account while it is enforced) with a challenge instead of a
 * session; these calls finish it, and manage it from Account settings.
 */

/** What POST /user/login answers instead of a session when a code is due. */
export interface MfaChallenge {
  mfa_required: true
  challenge: string
  /** The account must set up its authenticator app before the code. */
  enrol: boolean
  expires_in: number
}

export type LoginReply = LoginResponse | MfaChallenge

export const isMfaChallenge = (reply: LoginReply | undefined | null): reply is MfaChallenge =>
  !!reply && (reply as MfaChallenge).mfa_required === true

/** A new authenticator secret, drawn as a QR code. */
export interface MfaSecret {
  secret: string
  otpauth_uri: string
  account: string
}

export interface MfaStatus {
  available: boolean
  enforced: boolean
  enrolled: boolean
  enrolled_at?: string
  recovery_codes_left: number
  impersonating: boolean
}

/** Finish a login with a code from the app or a recovery code. */
export const submitLoginCode = async (challenge: string, code: string, remember: boolean) =>
  (await axios.post<LoginResponse>(`${API_BASE_URL}/user/login/mfa`, { challenge, code, remember })).data

/** Enforced and not set up: start set-up at login. */
export const startLoginEnrol = async (challenge: string) =>
  (await axios.post<MfaSecret>(`${API_BASE_URL}/user/login/mfa/enrol`, { challenge })).data

/** The first code sets it up: the session plus the recovery codes, shown once. */
export const confirmLoginEnrol = async (challenge: string, code: string, remember: boolean) =>
  (await axios.post<LoginResponse & { recovery_codes: string[] }>(`${API_BASE_URL}/user/login/mfa/enrol/confirm`, { challenge, code, remember })).data

export const getMfaStatus = async () => (await axios.get<MfaStatus>(`${API_BASE_URL}/user/mfa`)).data

export const startEnrol = async () => (await axios.post<MfaSecret>(`${API_BASE_URL}/user/mfa/enrol`)).data

export const confirmEnrol = async (code: string) =>
  (await axios.post<{ recovery_codes: string[] }>(`${API_BASE_URL}/user/mfa/enrol/confirm`, { code })).data

export const regenerateRecoveryCodes = async (code: string) =>
  (await axios.post<{ recovery_codes: string[] }>(`${API_BASE_URL}/user/mfa/recovery-codes`, { code })).data

/** Manager only: clear a user's app and recovery codes and end their session. */
export const resetUserMfa = async (userId: string) =>
  (await axios.post<{ reset: boolean, had_mfa: boolean }>(`${API_BASE_URL}/admin/users/${encodeURIComponent(userId)}/mfa/reset`)).data

/** The server's message for a failed two-step call, in words a person can act on. */
export const mfaErrorMessage = (e: unknown): string => {
  const data = (e as { response?: { data?: { message?: string, error?: string } } })?.response?.data
  if (data?.message) return data.message
  if (data?.error && !data.error.startsWith('mfa_')) return data.error
  return 'Something went wrong. Try again.'
}

/** The challenge is gone (expired, used, or too many wrong codes): back to the password. */
export const isChallengeExpired = (e: unknown): boolean =>
  (e as { response?: { data?: { error?: string } } })?.response?.data?.error === 'mfa_challenge_expired'
